import { useEffect, useRef, useState } from "react";

const MultiSelectControl = ({ options, value, name, onChange, placeholder, showSearch = true, className = '', showDots = false }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [isFocus, setIsFocus] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    let selectedItems = value.map(v => {
      return options.find(option => {
        return option.value === v;
      });
    });
    selectedItems = selectedItems.filter(item => {
      return options.map(v => v.value).includes(item?.value)
    });
    setSelectedItems(selectedItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  useEffect(() => {
    if (!isFocus) {
      return;
    }
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setActiveItemIndex(null);
        setIsFocus(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isFocus]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (displayOptions[activeItemIndex]) {
        addSelection(displayOptions[activeItemIndex].value);
      }
      setIsFocus(false);
    }
    if (event.key === 'ArrowDown' && activeItemIndex < displayOptions.length - 1) {
      event.preventDefault();
      setActiveItemIndex(activeItemIndex + 1);
    }
    if (event.key === 'ArrowUp' && activeItemIndex > 0) {
      event.preventDefault();
      setActiveItemIndex(activeItemIndex - 1);
    }
  };

  const filterOptions = (options) => {

    const searchOptions = (key) => {
      let isFound = false;
      selectedItems.forEach(o => {
        if (o?.value === key) {
          isFound = true;
        }
      });
      return isFound;
    }

    return options.filter((o) => { return o.label.toLowerCase().includes(searchString.toLowerCase()) && !searchOptions(o.value) });
  };

  const addSelection = (optionId) => {
      const selection = options.find((x) => String(x.value) === String(optionId));
      if (!selection) {
        return;
      }
      const updatedItems = [...selectedItems, selection];
      onChange(updatedItems.map(v => v.value));
      setIsFocus(false);
      setSelectedItems(updatedItems);
      setSearchString('');
  }

  const removeSelection = (optionId, event) => {
    event.preventDefault();
    const updatedItems = selectedItems.filter((x) => String(x.value) !== String(optionId));
    onChange(updatedItems.map(v => v.value));
    setSelectedItems(updatedItems);
  }

  const handleClearSelection = (event) => {
    event.preventDefault();
    setSelectedItems([]);
    onChange([]);
  }

  const handleInputFocus = () => {
    setActiveItemIndex(0);
    setIsFocus(!isFocus);
  }

  const handleSearch = (searchStr) => {
    setIsFocus(true);
    setSearchString(searchStr);
  }

  const handleInputBlur = () => {
    // ignore if mouse location inside dropdown
    if (!isHovering) {
      setActiveItemIndex(null);
      setIsFocus(false);
    }
  }

  const displayOptions = filterOptions(options);
  const dropdownSize = Math.max(Math.min(displayOptions.length, 8), 1);
  const useCustomDropdown = showSearch || showDots;

  return (
        <div ref={containerRef} className={`multi-select-control ${className}`.trim()}>
            <div
                className="input-control multi-select-search"
                onKeyDown={(e) => handleKeyDown(e)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={() => {
                  if (!showSearch) {
                    handleInputFocus();
                  }
                }}
            >
                {selectedItems.map((item, index) => {
                    const statusClass = showDots && item?.value
                      ? `status-${String(item.value).toLowerCase().replace(/_/g, '-')}`
                      : '';
                    return (
                      <span key={index} className={`multi-select-option ${statusClass}`}>
                        {showDots && <span className="multi-select-option-dot" aria-hidden="true"></span>}
                        <span className="label">{item?.label}</span>
                        <button onClick={(event) => removeSelection(item.value, event)}>x</button>
                      </span>
                    );
                })}
                {searchString.length === 0 && selectedItems.length === 0 && !useCustomDropdown && (
                  <span style={{ position: 'absolute', marginLeft: '10px', opacity: '80%' }}>
                    {placeholder || 'Select..'}
                  </span>
                )}
                {showSearch && (
                  <input
                      type="text"
                      className="multi-select-searchbar"
                      value={searchString}
                      placeholder={useCustomDropdown && selectedItems.length === 0 ? (placeholder || 'Select..') : ''}
                      onClick={() => handleInputFocus()} onBlur={() => handleInputBlur()} onChange={(e) => handleSearch(e.target.value)} />
                )}
                {selectedItems.length > 1 && <button className="clear-selection" onClick={(e) => handleClearSelection(e)}>X</button>}
                <i className="bx bx-chevron-down" style={{ fontSize: '22px', fontWeight: 'bold' }} onClick={() => handleInputFocus()} />
            </div>
            {useCustomDropdown ? (
              <div
                className={`multi-select-options ${isFocus ? 'is-open' : ''}`}
                role="listbox"
                onMouseEnter={() => setIsHovering(true)}
              >
                {displayOptions.map((o, i) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`multi-select-option-item ${activeItemIndex === i ? 'activeMulti' : ''}`}
                    onMouseEnter={() => setActiveItemIndex(i)}
                    onClick={() => addSelection(o.value)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            ) : (
              <select
                name={name}
                className={!isFocus ? 'multi-select-options' : ''}
                size={dropdownSize}
                multiple
                onChange={(e) => addSelection(e.target.value)}
                onMouseEnter={() => setIsHovering(true)}
              >
                {displayOptions.map((o, i) => (
                  <option
                    key={o.value}
                    value={o.value}
                    onMouseEnter={() => setActiveItemIndex(i)}
                    onClick={() => addSelection(o.value)}
                    className={activeItemIndex === i ? 'activeMulti' : ''}
                  >
                    {o.label}
                  </option>
                ))}
              </select>
            )}
        </div>
    );
}

export default MultiSelectControl;
